import type {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import type {CidIdMap} from 'notebook/notes/NoteConstants';
import type {NoteLinkedObjectType, NotePart} from 'notebook/util/NoteUtilConstants';

export type BaseNoteLinkedData<TIds, TData = unknown> = {
  name: string;
  type: NoteLinkedObjectType.NOTE_LINKED_DATA | NoteLinkedObjectType.NOTE_LINKED_PLATE_DESIGN;
  data: TData;
} & NLOId<TIds>;
export type ToServerNoteLinkedData<TData = unknown> = BaseNoteLinkedData<ToServerId, TData>;
export type FromServerNoteLinkedData<TData = unknown> = BaseNoteLinkedData<FromServerId, TData>;
export type NoteLinkedData<TData = unknown> = BaseNoteLinkedData<ClientId, TData>;
export type NoteLinkedDataNotePart<TData> = NotePart & {item: NoteLinkedData<TData>};

/**
 * Helper to prepare a NoteLinkedData NLO for saving to the server.
 *
 * This involves hydrating the "id" field using the cidIdMap.
 *
 * For NoteLinkedData where the client and server representations of the data
 * are the same, this function can be directly invoked in `prepareNLOForSave`.
 *
 * Otherwise, the "data" can be explicitly manipulated in a wrapping function:
 * @example
 * function prepareNoteLinkedFooForSave(
 *   item: NoteLinkedData<FooData>,
 *   cidIdMap: CidIdMap
 * ): ToServerNoteLinkedData<ToServerFooData> {
 *   return {
 *     ...prepareForSave<FooData>(item, cidIdMap),
 *     data: customDataTransform(item.data),
 *   };
 * }
 *
 * @param item the client representation of a NoteLinkedData
 * @param cidIdMap
 * @returns the server representation of the NoteLinkedData
 */
export function prepareForSave<TData = unknown>(
  item: NoteLinkedData<TData>,
  cidIdMap: CidIdMap
): ToServerNoteLinkedData<TData> {
  return {
    // For NLO's, cidIdMap will map to a uuid instead of a db id.
    id: cidIdMap[item.cid] as string | undefined,
    type: item.type,
    cid: item.cid,
    name: item.name,
    data: item.data,
  };
}
